import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Instagram } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = 'https://uandfashion.eu';

const InstagramHubPage = () => {
  const { t } = useLanguage();
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/looks`)
      .then((res) => setLooks(Array.isArray(res.data) ? res.data : []))
      .catch(() => setLooks([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="instagram-hub-page">
      <SEO
        title={t('Shop de Looks | U&Fashion op Instagram', 'Shop the Looks | U&Fashion on Instagram')}
        description={t(
          'Alle looks die we op Instagram deelden op één plek — klik door en shop direct bij onze partner-merken.',
          'All the looks we shared on Instagram in one place — click through and shop directly with our partner brands.'
        )}
        canonical={`${SITE_URL}/instagram`}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-pink-50/60 via-purple-50/40 to-indigo-50/40 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-14 text-center">
          <a
            href="https://instagram.com/uandfashion.eu"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur-sm text-pink-600 text-xs font-medium mb-5 shadow-sm hover:bg-white transition"
            data-testid="instagram-hub-handle"
          >
            <Instagram className="w-3.5 h-3.5" />
            @uandfashion.eu
          </a>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-gray-900 leading-[1.1] tracking-tight mb-6">
            {t('Shop de Looks', 'Shop the Looks')}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed font-light max-w-2xl mx-auto">
            {t(
              'Alle looks die we op Instagram deelden op één plek. Klik door en shop direct bij onze partner-merken.',
              'All the looks we shared on Instagram in one place. Click through and shop directly with our partner brands.'
            )}
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
        {loading ? (
          <div className="text-center text-gray-500 py-12">{t('Laden…', 'Loading…')}</div>
        ) : looks.length === 0 ? (
          <div className="text-center text-gray-500 py-16" data-testid="instagram-hub-empty">
            <p className="text-lg mb-3">
              {t('Nog geen looks beschikbaar.', 'No looks available yet.')}
            </p>
            <p className="text-sm text-gray-400">
              {t('De eerste Reel-look komt eraan.', "The first Reel-look is on the way.")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-testid="instagram-hub-grid">
            {looks.map((look) => (
              <Link
                key={look.id}
                to={`/look/${look.slug}`}
                className="group relative aspect-square rounded-2xl overflow-hidden bg-gray-100 shadow-sm hover:shadow-lg transition-all"
                data-testid={`instagram-hub-look-${look.slug}`}
              >
                <img
                  src={look.hero_image}
                  alt={look.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 text-white">
                  <p className="text-[10px] uppercase tracking-[0.3em] opacity-80 mb-1">
                    {t('Shop de look', 'Shop the look')}
                  </p>
                  <h2 className="text-base md:text-lg font-medium leading-tight">{look.title}</h2>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default InstagramHubPage;
